import ClearIcon from '@mui/icons-material/Clear';
import { Button, CircularProgress, Grid, IconButton, InputAdornment, Stack, TextField } from "@mui/material";
import { RegistrantFilters } from "./registrant-selection.hook";
import { useStyles } from "./registrant-selection.style";
import { useTranslation } from '@pruforce/common-adminweb-sdk';

export interface RegistrantFilterProps {
  filters: RegistrantFilters;
  searching: boolean;
  onChange: <K extends keyof RegistrantFilters>(key: K, value: RegistrantFilters[K]) => void;
  onSearch: () => Promise<void>;
  onReset: () => void;
}

export const RegistrantFilter = (props: RegistrantFilterProps) => {
  const t = useTranslation();
  const { filters, searching, onChange, onSearch, onReset } = props;
  const { classes } = useStyles();

  const renderClearAdornment = <K extends keyof RegistrantFilters>(key: K, value: RegistrantFilters[K]) => {
    if (!value) {
      return undefined;
    }

    return {
      endAdornment: (
        <InputAdornment position="end">
          <IconButton
            aria-label="clear"
            edge="end"
            size="small"
            className={classes.clearFilterButton}
            onClick={() => onChange(key, '' as RegistrantFilters[K])}
            disabled={searching}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        </InputAdornment>
      ),
    };
  };

  return (
    <div className={classes.sectionContainer}>
      <Grid container spacing={1.25}>
        <Grid item xs={3}>
          <TextField
            fullWidth
            label={t("recruitment.exam.registration.adminRegistration.registrantSelection.filter.firstNameExactMatch")}
            variant="outlined"
            size="small"
            value={filters.firstName}
            onChange={(event) => onChange("firstName", event.target.value)}
            InputProps={renderClearAdornment("firstName", filters.firstName)}
          />
        </Grid>

        <Grid item xs={3}>
          <TextField
            fullWidth
            label={t("recruitment.exam.registration.adminRegistration.registrantSelection.filter.lastNameExactMatch")}
            variant="outlined"
            size="small"
            value={filters.lastName}
            onChange={(event) => onChange("lastName", event.target.value)}
            InputProps={renderClearAdornment("lastName", filters.lastName)}
          />
        </Grid>

        <Grid item xs={3}>
          <TextField
            fullWidth
            label={t("recruitment.exam.registration.adminRegistration.registrantSelection.filter.nameExactMatch")}
            variant="outlined"
            size="small"
            value={filters.chiName}
            onChange={(event) => onChange("chiName", event.target.value)}
            InputProps={renderClearAdornment("chiName", filters.chiName)}
          />
        </Grid>

        <Grid item xs={3}>
          <TextField
            fullWidth
            label={t("recruitment.exam.registration.adminRegistration.registrantSelection.filter.identityDocumentNumberExactMatch")}
            variant="outlined"
            size="small"
            value={filters.identityDocumentNumber}
            onChange={(event) => onChange("identityDocumentNumber", event.target.value)}
            InputProps={renderClearAdornment("identityDocumentNumber", filters.identityDocumentNumber)}
          />
        </Grid>
      </Grid>
      <Grid container spacing={1.25}>
        <Grid item xs={3}>
          <TextField
            fullWidth
            label={t("recruitment.exam.registration.adminRegistration.registrantSelection.filter.candidateIdFuzzySearch")}
            variant="outlined"
            size="small"
            value={filters.candidateCode}
            onChange={(event) => onChange("candidateCode", event.target.value)}
            InputProps={renderClearAdornment("candidateCode", filters.candidateCode)}
          />
        </Grid>

        <Grid item xs={3}>
          <TextField
            fullWidth
            label={t("recruitment.exam.registration.adminRegistration.registrantSelection.filter.agentCodeFuzzySearch")}
            variant="outlined"
            size="small"
            value={filters.agentCode}
            onChange={(event) => onChange("agentCode", event.target.value)}
            InputProps={renderClearAdornment("agentCode", filters.agentCode)}
          />
        </Grid>

        <Grid item xs={3}>
          <TextField
            fullWidth
            label={t("recruitment.exam.registration.adminRegistration.registrantSelection.filter.recruiterCodeFuzzySearch")}
            variant="outlined"
            size="small"
            value={filters.recruiterCode}
            onChange={(event) => onChange("recruiterCode", event.target.value)}
            InputProps={renderClearAdornment("recruiterCode", filters.recruiterCode)}
          />
        </Grid>

        <Grid item xs={3} display="flex" alignItems="center">
          <Stack direction="row" gap={1.25} sx={{ pt: 0.5 }} >
            <Button
              className={classes.actionButton}
              variant="contained"
              color="secondary"
              disabled={searching}
              onClick={onSearch}
            >
              {t('app.button.search')}
              {searching ? <CircularProgress color="inherit" sx={{ ml: 1 }} size={16} /> : null}
            </Button>
            <Button
              className={classes.actionButton}
              variant="contained"
              color="inherit"
              disabled={searching}
              onClick={onReset}
            >
              {t('app.button.reset')}
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </div>
  );
};
